import * as React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { LucideIcon } from 'lucide-react';

interface MenuItem {
  icon: LucideIcon | React.FC;
  label: string;
  href: string;
  gradient: string;
  iconColor: string;
}

interface MenuBarProps {
  items: MenuItem[];
  activeItem?: string;
  onItemClick?: (label: string) => void;
  className?: string;
}

const itemVariants: any = {
  initial: { rotateX: 0, opacity: 1 },
  hover: { rotateX: -90, opacity: 0 },
};

const backVariants: any = {
  initial: { rotateX: 90, opacity: 0 },
  hover: { rotateX: 0, opacity: 1 },
};

const glowVariants: any = {
  initial: { opacity: 0, scale: 0.8 },
  hover: {
    opacity: 1,
    scale: 2,
    transition: {
      opacity: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
      scale: { duration: 0.5, type: 'spring', stiffness: 300, damping: 25 },
    },
  },
};

const navGlowVariants: any = {
  initial: { opacity: 0 },
  hover: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const sharedTransition: any = {
  type: 'spring',
  stiffness: 100,
  damping: 20,
  duration: 0.5,
};

export function MenuBar({ className, items, activeItem, onItemClick }: MenuBarProps) {
  return (
    <motion.nav
      className={cn(
        'p-1 rounded-2xl relative overflow-hidden',
        className,
      )}
      initial="initial"
      whileHover="hover"
    >
      <motion.div
        className="absolute -inset-2 rounded-3xl z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(245,138,37,0.15) 0%, transparent 70%)',
        }}
        variants={navGlowVariants}
      />
      <ul className="flex items-center gap-1.5 md:gap-3 relative z-10 list-none m-0 p-0">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.label === activeItem;

          return (
            <motion.li key={item.label} className="relative list-none">
              <button
                onClick={() => onItemClick?.(item.label)}
                className="block w-full bg-transparent border-none cursor-pointer p-0"
              >
                <motion.div
                  className="block rounded-xl overflow-visible group relative"
                  style={{ perspective: '600px' }}
                  whileHover="hover"
                  initial="initial"
                >
                  <motion.div
                    className="absolute inset-0 z-0 pointer-events-none"
                    variants={glowVariants}
                    animate={isActive ? 'hover' : 'initial'}
                    style={{
                      background: item.gradient,
                      opacity: isActive ? 1 : 0,
                      borderRadius: '16px',
                    }}
                  />
                  <motion.div
                    className={cn(
                      'flex items-center gap-2.5 px-4 md:px-5 py-2.5 relative z-10 bg-transparent transition-colors rounded-xl',
                      isActive ? 'text-white' : 'text-white/80 group-hover:text-white',
                    )}
                    variants={itemVariants}
                    transition={sharedTransition}
                    style={{
                      transformStyle: 'preserve-3d',
                      transformOrigin: 'center bottom',
                    }}
                  >
                    <span
                      className={cn(
                        'transition-colors duration-300',
                        isActive ? item.iconColor : 'text-white/80',
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="hidden md:inline text-sm font-semibold">{item.label}</span>
                  </motion.div>
                  <motion.div
                    className={cn(
                      'flex items-center gap-2.5 px-4 md:px-5 py-2.5 absolute inset-0 z-10 bg-transparent transition-colors rounded-xl',
                      isActive ? 'text-white' : 'text-white/80 group-hover:text-white',
                    )}
                    variants={backVariants}
                    transition={sharedTransition}
                    style={{
                      transformStyle: 'preserve-3d',
                      transformOrigin: 'center top',
                    }}
                  >
                    <span
                      className={cn(
                        'transition-colors duration-300',
                        isActive ? item.iconColor : 'text-white/80',
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="hidden md:inline text-sm font-semibold">{item.label}</span>
                  </motion.div>
                </motion.div>
              </button>
            </motion.li>
          );
        })}
      </ul>
    </motion.nav>
  );
}
